/**
 * Ask Cara — in-platform assistant page.
 *
 * Layout: verbatim from the user's prototype (role tabs, role pills,
 * suggestion chips, typing dots, footer disclaimer). Colors: re-skinned
 * to the CaraLoom brand tokens (`cn-bg`, `cn-surface`, `cn-primary`,
 * `cn-accent`, `cn-text`, `cn-border`) instead of the prototype's
 * one-off teal/coral palette. Dark-mode aware via the same tokens.
 *
 * Data flow + guardrails are unchanged: `/api/cara/chat[-public]` →
 * Vertex BAA path; live mode reads role-scoped real data; sample mode
 * uses the prototype demo context. See routes/cara.py.
 *
 * Data-testids: ask-cara-page, ask-cara-header, ask-cara-role-{key},
 * ask-cara-input, ask-cara-send, ask-cara-suggestion-{i},
 * ask-cara-message-{i}, ask-cara-loading, ask-cara-mode-badge.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Seo } from '../components/seo/Seo';
import { resolveCaraRole } from '../lib/cara_roles';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cara avatar — a minimal weave/knot mark drawn in the brand accent
 * (Neural Cyan over Midnight Navy). Reads from CSS variables so it
 * tracks light/dark mode automatically.
 */
function CaraMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" aria-hidden="true">
      <g fill="none">
        <path d="M4 6 V20 M9 6 V20 M14 6 V20 M19 6 V20"
              stroke="var(--cn-text-muted)" strokeWidth="1.1" />
        <path d="M2 9 C7 9 7 13 12 13 S17 17 24 17"
              stroke="var(--cn-accent)" strokeWidth="1.8" />
      </g>
    </svg>
  );
}

export default function AskCara() {
  const { user, getAuthHeader } = useAuth();
  const isAuthed = !!user;
  const userRole = resolveCaraRole(user?.role);

  const [meta, setMeta] = useState(null);
  const [role, setRole] = useState(userRole || 'planner');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auth context loads `user` asynchronously — on the first render
  // `userRole` is still null even for signed-in users, so the
  // `useState(userRole || 'planner')` above pins `role` at 'planner'
  // (the fallback) and never resyncs. That kept Cara stuck in
  // sample mode after login. Snap `role` to `userRole` exactly ONCE
  // the moment it first becomes available; subsequent user tab clicks
  // are respected because the ref guard fires only on the transition.
  const roleSynced = useRef(false);
  useEffect(() => {
    if (userRole && !roleSynced.current) {
      setRole(userRole);
      roleSynced.current = true;
    }
  }, [userRole]);

  const mode = isAuthed && userRole && role === userRole ? 'live' : 'sample';
  const endpoint = mode === 'live' ? `${API}/cara/chat` : `${API}/cara/chat-public`;

  // Role-tab visibility (iter133):
  //   • unauthenticated visitors  → all 6 tabs (marketing / demo surface)
  //   • signed-in admin           → all 6 tabs (cross-role visibility)
  //   • signed-in anyone else     → only their own role tab
  //
  // Prevents the confusing "signed-in clinician clicks Family tab →
  // sample mode looks like a bug" flow. Admins keep the full picker
  // because their live-context builder is cross-tenant by design and
  // they legitimately need to preview other roles for support triage.
  const isAdmin = userRole === 'admin';
  const visibleRoles = (meta?.roles || []).filter((r) => {
    if (!isAuthed) return true;
    if (isAdmin) return true;
    return r.key === userRole;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/cara/meta`);
        if (!cancelled) {
          setMeta(r.data);
          const cfg = r.data.roles.find((x) => x.key === role);
          if (cfg && messages.length === 0) {
            setMessages([{ who: 'cara', text: cfg.greeting }]);
          }
          // If we arrived here from a Cara nudge with a prefilled prompt,
          // surface it in the input. We don't auto-send so the user can
          // edit before talking to Cara — a small consent step.
          try {
            const prefilled = sessionStorage.getItem('cara_prefilled_prompt');
            if (prefilled) {
              setInput(prefilled);
              sessionStorage.removeItem('cara_prefilled_prompt');
            }
          } catch { /* sessionStorage may be unavailable */ }
        }
      } catch (e) {
        if (!cancelled) {
          setMeta({ roles: [] });
          setMessages([{ who: 'cara', text: "Hi — I'm Cara." }]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const switchRole = useCallback((r) => {
    setRole(r);
    setInput('');
    const cfg = meta?.roles?.find((x) => x.key === r);
    setMessages(cfg ? [{ who: 'cara', text: cfg.greeting }] : []);
  }, [meta]);

  const send = useCallback(async (override) => {
    const content = (override ?? input).trim();
    if (!content || loading) return;
    const next = [...messages, { who: 'user', text: content }];
    setMessages(next);
    setInput('');
    setLoading(true);

    const apiMessages = next
      .map((m) => ({ role: m.who === 'user' ? 'user' : 'assistant', content: m.text }))
      .filter((_, i) => !(i === 0 && next[0].who === 'cara'));

    try {
      const headers = mode === 'live' ? (getAuthHeader()?.headers || {}) : {};
      const { data } = await axios.post(endpoint, {
        messages: apiMessages, role, mode,
      }, { headers });
      setMessages((m) => [...m, { who: 'cara', text: data.reply || "Sorry — I couldn't generate a reply. Try again?" }]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 503) msg = "Cara's BAA-locked AI path is temporarily unavailable. Please retry in a moment.";
      else if (typeof detail === 'string') msg = detail;
      else msg = "I hit a connection error. Please try again.";
      setMessages((m) => [...m, { who: 'cara', text: msg }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, mode, endpoint, role, getAuthHeader]);

  const activeRoleCfg = meta?.roles?.find((x) => x.key === role) || {
    label: '—', eyebrow: '', greeting: '', suggestions: [],
  };
  const showSuggestions = messages.length <= 1 && activeRoleCfg.suggestions?.length;
  const isLive = mode === 'live';

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 font-sans"
      style={{ background: 'var(--cn-bg)' }}
      data-testid="ask-cara-page"
    >
      <Seo title="Ask Cara — CaraLoom" description="In-platform assistant for hospital planners, providers, and families." />
      <div
        className="w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col"
        style={{
          height: 'min(88vh, 760px)',
          background: 'var(--cn-surface)',
          border: '1px solid var(--cn-border)',
          boxShadow: '0 20px 60px -25px rgba(15,23,42,0.18), 0 8px 24px -8px rgba(15,23,42,0.08)',
        }}
      >

        {/* header */}
        <div
          className="px-5 py-4"
          style={{ background: 'var(--cn-primary)', borderBottom: '1px solid var(--cn-border)' }}
          data-testid="ask-cara-header"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(6, 182, 212, 0.12)' }}
            >
              <CaraMark size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold tracking-tight leading-none text-lg">Ask Cara</div>
              <div className="text-[11px] mt-1 tracking-wide truncate" style={{ color: 'var(--cn-accent)' }}>
                {activeRoleCfg.eyebrow}
              </div>
            </div>
            <span
              data-testid="ask-cara-mode-badge"
              className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 border whitespace-nowrap"
              style={{
                color: isLive ? '#34D399' : 'var(--cn-accent)',
                borderColor: isLive ? 'rgba(52,211,153,0.4)' : 'rgba(6,182,212,0.4)',
                background: isLive ? 'rgba(52,211,153,0.08)' : 'rgba(6,182,212,0.08)',
              }}
            >
              {isLive ? 'Live · your data' : 'Demo · sample data'}
            </span>
          </div>
          <div className="flex gap-1.5 mt-3 flex-wrap">
            {visibleRoles.map((val) => {
              const active = role === val.key;
              return (
                <button
                  key={val.key}
                  onClick={() => switchRole(val.key)}
                  data-testid={`ask-cara-role-${val.key}`}
                  className="text-xs px-3 py-1.5 rounded-full transition"
                  style={active ? {
                    background: 'var(--cn-accent)',
                    color: 'var(--cn-primary)',
                    fontWeight: 600,
                  } : {
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.72)',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  {val.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
          style={{ background: 'var(--cn-bg)' }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`ask-cara-message-${i}`}
              className={`flex ${m.who === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {m.who === 'cara' && (
                <div
                  className="mr-2 mt-1 shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--cn-primary)' }}
                >
                  <CaraMark size={15} />
                </div>
              )}
              <div
                className="max-w-[80%] px-4 py-2.5 rounded-2xl text-[14.5px] leading-relaxed whitespace-pre-wrap"
                style={m.who === 'user' ? {
                  background: 'var(--cn-primary)',
                  color: 'var(--cn-surface)',
                  borderBottomRightRadius: 6,
                } : {
                  background: 'var(--cn-surface)',
                  color: 'var(--cn-text)',
                  border: '1px solid var(--cn-border)',
                  borderBottomLeftRadius: 6,
                }}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="ask-cara-loading">
              <div
                className="mr-2 mt-1 shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: 'var(--cn-primary)' }}
              >
                <CaraMark size={15} />
              </div>
              <div
                className="rounded-2xl px-4 py-3"
                style={{
                  background: 'var(--cn-surface)',
                  border: '1px solid var(--cn-border)',
                  borderBottomLeftRadius: 6,
                }}
              >
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--cn-text-muted)', animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--cn-text-muted)', animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--cn-text-muted)', animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* suggestions */}
        {showSuggestions && (
          <div className="px-5 pt-3 flex flex-wrap gap-2" style={{ background: 'var(--cn-bg)' }}>
            {activeRoleCfg.suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                data-testid={`ask-cara-suggestion-${i}`}
                className="text-[12.5px] rounded-full px-3 py-1.5 transition inline-flex items-center gap-1.5"
                style={{
                  background: 'var(--cn-surface)',
                  color: 'var(--cn-text)',
                  border: '1px solid var(--cn-border)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--cn-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--cn-border)'; }}
              >
                <Sparkles className="w-3 h-3" style={{ color: 'var(--cn-accent)' }} />
                {s}
              </button>
            ))}
          </div>
        )}

        {/* input */}
        <div className="px-4 py-3" style={{ background: 'var(--cn-bg)', borderTop: '1px solid var(--cn-border)' }}>
          <div
            className="flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5"
            style={{ background: 'var(--cn-surface)', border: '1px solid var(--cn-border)' }}
          >
            <input
              data-testid="ask-cara-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder={`Ask Cara as ${activeRoleCfg.label.toLowerCase()}…`}
              className="flex-1 outline-none text-[14.5px] bg-transparent"
              style={{ color: 'var(--cn-text)' }}
            />
            <button
              data-testid="ask-cara-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-40 transition"
              style={{ background: 'var(--cn-accent)', color: 'var(--cn-primary)' }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--cn-accent-hover)'; }}
              onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--cn-accent)'; }}
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="text-[10.5px] text-center mt-2 px-2" style={{ color: 'var(--cn-text-muted)' }}>
            Cara can&apos;t give medical or legal advice, and can&apos;t act on gates — a person always accepts placements and signs documents.
            {isLive
              ? ' Routed through the BAA-locked Vertex AI path. Audited.'
              : ' Demo on sample data — not real PHI.'}
          </div>
        </div>

      </div>
    </div>
  );
}
